/**
 * enPlaybook — the /playbook gift page: ready automations per business type.
 *
 * Key shape: `landing.pb.<kind>.<n>.<field>` with field ∈ name | trigger |
 * s1 | s2 | s3 | result | saved. `pages/Playbook/playbookData.ts` builds the
 * keys and `playbookData.test.ts` proves every one exists here.
 * Time-saved figures are estimates and are labelled as such on the page.
 */
export const enPlaybook = {
  'landing.pb.title': 'Your free Automation Playbook for {kind}',
  'landing.pb.subtitle': 'Ready-made automations for {kind} — the exact trigger, the steps, and what you get.',
  'landing.pb.trigger': 'Trigger',
  'landing.pb.steps': 'Steps',
  'landing.pb.result': 'Result',
  'landing.pb.saved': 'Estimated time saved',
  'landing.pb.savedNote': 'Time saved is an estimate — it depends on how many messages you get.',
  'landing.pb.cta': 'Get started free',
  'landing.pb.ctaNote': 'No credit card required',
  'landing.pb.pickerTitle': 'Get your free Automation Playbook',
  'landing.pb.pickerSubtitle': 'Pick your business type to see the automations that fit how you sell.',
  'landing.pb.otherKinds': 'Playbooks for other businesses',
  'landing.pb.back': 'BuzzBit X home',
  'landing.pb.eyebrow': 'Free playbook',
  'landing.pb.kind.ecommerce': 'online shops',
  'landing.pb.kind.services': 'services & clinics',
  'landing.pb.kind.coaching': 'coaches',
  'landing.pb.kind.courses': 'course creators',
  'landing.pb.kind.saas': 'SaaS companies',
  'landing.pb.kind.creators': 'content creators',

  // ── E-commerce ───────────────────────────────────────────────────────────
  'landing.pb.ecommerce.1.name': 'Comment-to-DM product link',
  'landing.pb.ecommerce.1.trigger': 'Someone comments DROP on your product post',
  'landing.pb.ecommerce.1.s1': 'Public reply: “Check your DMs!”',
  'landing.pb.ecommerce.1.s2': 'DM with the product link and buttons',
  'landing.pb.ecommerce.1.s3': 'The customer is tagged as interested',
  'landing.pb.ecommerce.1.result': 'Comments turn into shoppers with a link in hand',
  'landing.pb.ecommerce.1.saved': '~3 hours a week',
  'landing.pb.ecommerce.2.name': 'Abandoned-cart recovery',
  'landing.pb.ecommerce.2.trigger': 'A shopper leaves items in the cart',
  'landing.pb.ecommerce.2.s1': 'Wait 1 hour',
  'landing.pb.ecommerce.2.s2': 'Reminder email showing the cart',
  'landing.pb.ecommerce.2.s3': 'A second reminder a day later',
  'landing.pb.ecommerce.2.result': 'Carts recovered without lifting a finger',
  'landing.pb.ecommerce.2.saved': '~2 hours a week',
  'landing.pb.ecommerce.3.name': 'Post-purchase review',
  'landing.pb.ecommerce.3.trigger': 'An order is fulfilled',
  'landing.pb.ecommerce.3.s1': 'Wait 7 days',
  'landing.pb.ecommerce.3.s2': 'Email asking for a review',
  'landing.pb.ecommerce.3.s3': 'Thank-you with a code for next time',
  'landing.pb.ecommerce.3.result': 'More reviews and more repeat orders',
  'landing.pb.ecommerce.3.saved': '~1 hour a week',
  'landing.pb.ecommerce.4.name': 'WhatsApp order questions',
  'landing.pb.ecommerce.4.trigger': 'A customer messages you on WhatsApp',
  'landing.pb.ecommerce.4.s1': 'Buttons: shipping, returns, talk to a human',
  'landing.pb.ecommerce.4.s2': 'Instant answers to the common questions',
  'landing.pb.ecommerce.4.s3': 'Handoff to your inbox when it needs you',
  'landing.pb.ecommerce.4.result': 'Support that answers in seconds',
  'landing.pb.ecommerce.4.saved': '~4 hours a week',

  // ── Services & clinics ───────────────────────────────────────────────────
  'landing.pb.services.1.name': 'Book straight from a DM',
  'landing.pb.services.1.trigger': 'Someone writes “book” on Instagram or WhatsApp',
  'landing.pb.services.1.s1': 'Buttons to pick a service',
  'landing.pb.services.1.s2': 'Open slots shown in their local time',
  'landing.pb.services.1.s3': 'Booking confirmed with a calendar invite',
  'landing.pb.services.1.result': 'Appointments booked while you work',
  'landing.pb.services.1.saved': '~4 hours a week',
  'landing.pb.services.2.name': 'No-show reminders',
  'landing.pb.services.2.trigger': 'A booking is made',
  'landing.pb.services.2.s1': 'Reminder 24 hours before',
  'landing.pb.services.2.s2': 'Reply to confirm or reschedule',
  'landing.pb.services.2.s3': 'Your calendar updates itself',
  'landing.pb.services.2.result': 'Fewer no-shows and empty slots',
  'landing.pb.services.2.saved': '~2 hours a week',
  'landing.pb.services.3.name': 'Rebook after the visit',
  'landing.pb.services.3.trigger': 'An appointment is completed',
  'landing.pb.services.3.s1': 'Thank-you message the same day',
  'landing.pb.services.3.s2': 'Rebooking link after your usual interval',
  'landing.pb.services.3.s3': 'Review request',
  'landing.pb.services.3.result': 'Clients come back on schedule',
  'landing.pb.services.3.saved': '~2 hours a week',
  'landing.pb.services.4.name': 'Prices and location FAQ',
  'landing.pb.services.4.trigger': 'A question about prices, hours or location',
  'landing.pb.services.4.s1': 'Buttons with your most-asked questions',
  'landing.pb.services.4.s2': 'Instant answer with a booking link',
  'landing.pb.services.4.s3': 'Handoff to a human at any time',
  'landing.pb.services.4.saved': '~3 hours a week',

  // ── Coaching ─────────────────────────────────────────────────────────────
  'landing.pb.coaching.1.name': 'Lead magnet by DM',
  'landing.pb.coaching.1.trigger': 'Someone comments GUIDE on your post',
  'landing.pb.coaching.1.s1': 'Public reply and a DM',
  'landing.pb.coaching.1.s2': 'Follow check before the gift',
  'landing.pb.coaching.1.s3': 'Guide delivered and email captured',
  'landing.pb.coaching.1.result': 'Followers become subscribers',
  'landing.pb.coaching.1.saved': '~3 hours a week',
  'landing.pb.coaching.2.name': 'Discovery call booking',
  'landing.pb.coaching.2.trigger': 'A lead taps “Book a call”',
  'landing.pb.coaching.2.s1': 'Two short qualifying questions',
  'landing.pb.coaching.2.s2': 'Pick a slot in their local time',
  'landing.pb.coaching.2.s3': 'BuzzCall video link and a reminder',
  'landing.pb.coaching.2.result': 'Qualified calls on your calendar',
  'landing.pb.coaching.2.saved': '~3 hours a week',
  'landing.pb.coaching.3.name': 'Subscriber nurture',
  'landing.pb.coaching.3.trigger': 'A new subscriber joins',
  'landing.pb.coaching.3.s1': 'Welcome email',
  'landing.pb.coaching.3.s2': 'Tips on day 3 and day 7',
  'landing.pb.coaching.3.s3': 'Invitation to your program',
  'landing.pb.coaching.3.result': 'Warm leads, ready to buy',
  'landing.pb.coaching.3.saved': '~2 hours a week',
  'landing.pb.coaching.4.name': 'After-call follow-up',
  'landing.pb.coaching.4.trigger': 'A discovery call ends',
  'landing.pb.coaching.4.s1': 'Thank-you with the next step',
  'landing.pb.coaching.4.s2': 'Offer email two days later',
  'landing.pb.coaching.4.s3': 'A nudge if there is no reply',
  'landing.pb.coaching.4.result': 'No lead left without a follow-up',
  'landing.pb.coaching.4.saved': '~2 hours a week',

  // ── Course creators ──────────────────────────────────────────────────────
  'landing.pb.courses.1.name': 'Launch waitlist',
  'landing.pb.courses.1.trigger': 'Someone joins the waitlist from a popup',
  'landing.pb.courses.1.s1': 'Confirmation email',
  'landing.pb.courses.1.s2': 'Countdown emails before launch',
  'landing.pb.courses.1.s3': 'Enrolment link on launch day',
  'landing.pb.courses.1.result': 'A launch that sells itself',
  'landing.pb.courses.1.saved': '~3 hours a week',
  'landing.pb.courses.2.name': 'Comment to enrol',
  'landing.pb.courses.2.trigger': 'A comment with your course keyword',
  'landing.pb.courses.2.s1': 'DM with the course page',
  'landing.pb.courses.2.s2': 'Buttons: syllabus, price, enrol',
  'landing.pb.courses.2.s3': 'Follow-up if they have not enrolled',
  'landing.pb.courses.2.result': 'Social attention becomes enrolments',
  'landing.pb.courses.2.saved': '~2 hours a week',
  'landing.pb.courses.3.name': 'Student onboarding',
  'landing.pb.courses.3.trigger': 'A student enrols',
  'landing.pb.courses.3.s1': 'Welcome and first lesson',
  'landing.pb.courses.3.s2': 'A nudge if they have not started',
  'landing.pb.courses.3.s3': 'Completion message and your next course',
  'landing.pb.courses.3.result': 'More students finish — and buy again',
  'landing.pb.courses.3.saved': '~2 hours a week',

  // ── SaaS ─────────────────────────────────────────────────────────────────
  'landing.pb.saas.1.name': 'Trial onboarding',
  'landing.pb.saas.1.trigger': 'A trial starts',
  'landing.pb.saas.1.s1': 'Day 0: welcome email',
  'landing.pb.saas.1.s2': 'Day 2: the one feature that matters',
  'landing.pb.saas.1.s3': 'Day 5: invitation to book a demo',
  'landing.pb.saas.1.result': 'Trials that turn into customers',
  'landing.pb.saas.1.saved': '~3 hours a week',
  'landing.pb.saas.2.name': 'Demo booking from a DM',
  'landing.pb.saas.2.trigger': 'A lead asks for a demo in a DM',
  'landing.pb.saas.2.s1': 'Buttons for team size and use case',
  'landing.pb.saas.2.s2': 'Open slots in their time zone',
  'landing.pb.saas.2.s3': 'BuzzCall link and a reminder',
  'landing.pb.saas.2.result': 'Demos booked without email ping-pong',
  'landing.pb.saas.2.saved': '~2 hours a week',
  'landing.pb.saas.3.name': 'Failed-payment recovery',
  'landing.pb.saas.3.trigger': 'A payment fails',
  'landing.pb.saas.3.s1': 'Friendly email with the billing link',
  'landing.pb.saas.3.s2': 'Reminder after 3 days',
  'landing.pb.saas.3.s3': 'At-risk customers tagged for your team',
  'landing.pb.saas.3.result': 'Less involuntary churn',
  'landing.pb.saas.3.saved': '~1 hour a week',

  // ── Creators ─────────────────────────────────────────────────────────────
  'landing.pb.creators.1.name': 'Keyword → link in DMs',
  'landing.pb.creators.1.trigger': 'A comment with LINK on your Reel',
  'landing.pb.creators.1.s1': 'Public reply under the comment',
  'landing.pb.creators.1.s2': 'DM with the link',
  'landing.pb.creators.1.s3': 'Follow check for bonus content',
  'landing.pb.creators.1.result': 'Every comment gets the link',
  'landing.pb.creators.1.saved': '~4 hours a week',
  'landing.pb.creators.2.name': 'Story replies → newsletter',
  'landing.pb.creators.2.trigger': 'Someone replies to your story',
  'landing.pb.creators.2.s1': 'Instant thank-you DM',
  'landing.pb.creators.2.s2': 'Ask for their email with a button',
  'landing.pb.creators.2.s3': 'Added to your newsletter',
  'landing.pb.creators.2.result': 'Your list grows from your stories',
  'landing.pb.creators.2.saved': '~2 hours a week',
  'landing.pb.creators.3.name': 'Brand-deal triage',
  'landing.pb.creators.3.trigger': 'A DM about a collab or partnership',
  'landing.pb.creators.3.s1': 'Buttons: brand, budget, timeline',
  'landing.pb.creators.3.s2': 'The details are collected for you',
  'landing.pb.creators.3.s3': 'Routed to your inbox with a tag',
  'landing.pb.creators.3.result': 'Deals sorted out of the fan mail',
  'landing.pb.creators.3.saved': '~2 hours a week',
} as const;
